import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoIosHome, IoMdAdd } from "react-icons/io";
import KruozAPI from "../../KruozAPI";
import { toast, Toaster } from "react-hot-toast";
import OrderSummaryCard from "./OrderSummaryCard";
import ProviderCard from "./ProviderCard";

const SectionCard = ({ title, children }) => (
  <div className="bg-white shadow-md rounded-lg p-6 mb-6">
    <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">
      {title}
    </h3>
    {children}
  </div>
);

const PriceSummaryCard = ({ priceSummary }) => {
  return (
    <SectionCard title="Price Summary">
      <div className="space-y-4">
        {priceSummary.breakdown.map((item, index) => (
          <div key={index} className="border-b pb-4 last:border-b-0">
            <div className="flex justify-between mb-2">
              <h4 className="font-medium text-gray-800">{item.fieldLabel}</h4>
              <span className="font-medium">${item.total.toFixed(2)}</span>
            </div>
            <div className="space-y-1">
              {item.details.map((detail, detailIndex) => (
                <div
                  key={detailIndex}
                  className="flex justify-between text-sm text-gray-600"
                >
                  <span>
                    {detail.optionLabel} - {detail.propertyLabel}
                  </span>
                  <span>${detail.price.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-between font-bold text-lg pt-2 border-t">
          <span>Total Price</span>
          <span>${priceSummary.totalPrice.toFixed(2)}</span>
        </div>
      </div>
    </SectionCard>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-wrap mb-2">
    <span className="w-full sm:w-1/3 font-medium text-gray-600">{label}:</span>
    <span className="w-full sm:w-2/3">{value || "N/A"}</span>
  </div>
);

const QuotationOrderHome = () => {
  const [quotationRequest, setQuotationRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { orderNumber } = useParams();
  const [providerInfo, setProviderInfo] = useState(null);
  const [orderNotFound, setOrderNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuotationRequest = async () => {
      try {
        const response = await KruozAPI.get(
          `/Quotation/QuotationRequestDetailsByOrderNumber?orderNumber=${orderNumber}`
        );
        debugger;
        setQuotationRequest(response.data);
        if (response.data?.quotationCompanyCode) {
          const companyResponse = await KruozAPI.get(
            `Company/QuotationCompany?code=${response.data.quotationCompanyCode}`
          );
          setProviderInfo(companyResponse.data);
        }
        setLoading(false);
      } catch (err) {
        //setError("Failed to fetch Quote request details");
        setLoading(false);
      }
    };

    fetchQuotationRequest();
  }, [orderNumber]);

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      if (value.Address) {
        return (
          <div>
            <div>{value.Address}</div>
            {value.City && (
              <div>
                {value.City}, {value.State} {value.PostalCode}
              </div>
            )}
            {value.Country && <div>{value.Country}</div>}
          </div>
        );
      }
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderSection = (step) => {
    if (!step || !Array.isArray(step.fields) || step.fields.length === 0) {
      return null; // Don't render anything if fields is not a valid array or is empty
    }
    const previewFields = step.fields.filter(
      (field) => field?.showInPreview !== false
    );

    if (previewFields.length === 0) return null;

    // Build list of valid fields (skip N/A/empty/null/undefined strings)
    const validFields = [];
    const seenGroups = new Set();
    const groupOrder = [];
    const groupMap = new Map();

    previewFields.forEach((field) => {
      const formatted = formatValue(field.value);
      if (typeof formatted === "string") {
        const f = formatted.trim().toLowerCase();
        if (f === "n/a" || f === "") return;
        if (f.includes("undefined") || f.includes("null")) return;
      }
      validFields.push({ field, formatted });
      const groupName = (field.group && String(field.group).trim()) || "";
      if (groupName) {
        if (!seenGroups.has(groupName)) {
          seenGroups.add(groupName);
          groupOrder.push(groupName);
        }
        if (!groupMap.has(groupName)) groupMap.set(groupName, []);
        groupMap.get(groupName).push({ field, formatted });
      }
    });

    if (validFields.length === 0) return null;

    // Ungrouped rows are those with no group
    const ungroupedRows = validFields
      .filter(({ field }) => !field.group || String(field.group).trim() === "")
      .map(({ field, formatted }) => (
        <InfoRow key={field.id} label={field.label} value={formatted} />
      ));

    return (
      <SectionCard key={step.id} title={step.label || step.id}>
        {ungroupedRows.length > 0 && (
          <div className="space-y-2">{ungroupedRows}</div>
        )}
        {groupOrder.map((groupName) => (
          <div
            key={`${step.id}-group-${groupName}`}
            className="mt-4 border border-gray-200 rounded-md p-4"
          >
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {groupName}
            </h4>
            <div className="space-y-2">
              {groupMap.get(groupName).map(({ field, formatted }) => (
                <InfoRow key={field.id} label={field.label} value={formatted} />
              ))}
            </div>
          </div>
        ))}
      </SectionCard>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  // if (!quotationRequest || !quotationRequest.content) {
  //   return (
  //     <OrderSummaryCard
  //       orderId={orderNumber}
  //       onNotFound={() => setOrderNotFound(true)}
  //     />
  //   );
  // }

  var quotationTemplate = null;
  if (quotationRequest && quotationRequest.content) {
    try {
      quotationTemplate = JSON.parse(quotationRequest.content);
    } catch (err) {
      console.error("Error parsing quotation request content:", err);
      quotationTemplate = null;
    }
  }


  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Toaster position="top-right" />

      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center space-x-2">
              {/* <span className="text-3xl">🗑️</span>
              <span className="text-2xl font-bold text-green-600">
                EasyHireQuotes
              </span> */}
              <img
                src={
                  process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL +
                  "EasyHireQuates.png"
                }
                alt="EasyHireQuotes"
                className="h-12 w-auto"
              />
            </div>
            {quotationRequest && quotationRequest.quotationCompanyCode && <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  navigate(`/${quotationRequest.quotationCompanyCode}`)
                }
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
              >
                <IoMdAdd className="w-4 h-4" />
                <span>Add New</span>
              </button>
              {/* <button
                onClick={handleBackHandle}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
              >
                <IoIosHome className="w-4 h-4" />
                <span>Home</span>
              </button> */}
            </div>}
          </div>
        </div>
      </header>

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-4">Order Details</h2>
          {providerInfo && <ProviderCard providerInfo={providerInfo} />}
          {orderNumber && (
            <OrderSummaryCard
              orderId={orderNumber}
              onNotFound={() => setOrderNotFound(true)}
            />
          )}
          {orderNotFound && (
            <div className="text-sm text-gray-500 mb-2">
              {/* When order is not found, we intentionally hide other details below. */}
            </div>
          )}
          {/* {quotationTemplate.orderSummary && (
            <OrderSummaryCard orderSummary={quotationTemplate.orderSummary} />
          )} */}

          {!orderNotFound && quotationTemplate && quotationTemplate.priceSummary && (
            <PriceSummaryCard priceSummary={quotationTemplate.priceSummary} />
          )}

          {!orderNotFound && quotationTemplate && quotationTemplate.wizardSteps && (
            quotationTemplate.wizardSteps.map((step) => renderSection(step))
          )}
        </div>
      </main>

      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4">
            © {new Date().getFullYear()} EasyHireQuotes. All rights reserved.
          </p>
          <div className="flex justify-center space-x-4">
            {/* <a
              href="/privacy-policy"
              className="hover:text-green-400 transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/terms-of-service"
              className="hover:text-green-400 transition-colors"
            >
              Terms of Service
            </a>
            <a
              href="/contact"
              className="hover:text-green-400 transition-colors"
            >
              Contact
            </a> */}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QuotationOrderHome;
